import { useState, useEffect } from 'react';
import {
  LayoutDashboard, FileText, Tag, Users, MessageSquare, Bell, Settings,
  BarChart3, Plus, ChevronRight, FileSpreadsheet, Search, Calendar,
  Download, TrendingUp, Eye, Edit, Trash2, X, Gift, LogOut, Image
} from 'lucide-react';
import CreateThought, { ThoughtFormData } from './CreateThought';
import ExcelUpload, { ImportedThought } from './ExcelUpload';
import CommentModeration from './CommentModeration';
import UserManagement from './UserManagement';
import Analytics from './Analytics';
import Announcements from './Announcements';
import SettingsComponent from './Settings';
import Occasions from './Occasions';
import ImageLibrary from './ImageLibrary';
import { adminAnalyticsApi, adminThoughtsApi, adminCategoriesApi, adminCommentsApi } from '../services/api';
import { toast } from 'sonner';

type TabType = 'dashboard' | 'thoughts' | 'categories' | 'users' | 'comments' | 'announcements' | 'analytics' | 'settings' | 'occasions' | 'images';

interface Stats {
  totalThoughts: number;
  totalUsers: number;
  totalComments: number;
  activeUsers: number;
  thoughtsToday: number;
  engagementRate: string;
}

interface AdminDashboardProps {
  onLogout: () => void;
}

export default function AdminDashboard({ onLogout }: AdminDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [showCreateThought, setShowCreateThought] = useState(false);
  const [showExcelUpload, setShowExcelUpload] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [stats, setStats] = useState<Stats>({
    totalThoughts: 0,
    totalUsers: 0,
    totalComments: 0,
    activeUsers: 0,
    thoughtsToday: 0,
    engagementRate: '0%',
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'scheduled' | 'published'>('all');
  const [filterCategory, setFilterCategory] = useState('');
  const [refreshThoughts, setRefreshThoughts] = useState(0);

  // Fetch stats from API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoadingStats(true);
        const [data, thoughtsData] = await Promise.all([
          adminAnalyticsApi.get(),
          adminThoughtsApi.getAll({ limit: 1000 }),
        ]);

        const realThoughtsCount = (thoughtsData.thoughts || []).filter(
          (t: any) => t.author !== 'Image Library' && !t.text?.startsWith('[Image Library]')
        ).length;

        // Map backend data to frontend stats
        setStats({
          totalThoughts: realThoughtsCount,
          totalUsers: data.analytics.totalUsers || 0,
          totalComments: data.analytics.totalComments || 0,
          activeUsers: data.analytics.activeUsers || 0,
          thoughtsToday: 0, // Backend doesn't provide this yet
          engagementRate: data.analytics.activeUsers && data.analytics.totalUsers 
            ? `${((data.analytics.activeUsers / data.analytics.totalUsers) * 100).toFixed(1)}%` 
            : '0%',
        });
        
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        toast.error('Failed to load stats. Using demo data.');
        // Keep default/demo stats on error
      } finally {
        setIsLoadingStats(false);
      }
    };

    if (activeTab === 'dashboard') {
      fetchStats();
    }
  }, [activeTab]);

  const handleSaveThought = (_thoughtData: ThoughtFormData) => {
    // Refresh thoughts list after save
    setRefreshThoughts(prev => prev + 1);
    // Update stats
    setStats(prev => ({
      ...prev,
      totalThoughts: prev.totalThoughts + 1,
      thoughtsToday: prev.thoughtsToday + 1,
    }));
  };

  const handleImportThoughts = (thoughts: ImportedThought[]) => {
    // Refresh thoughts list after import
    setRefreshThoughts(prev => prev + 1);
    // Update stats
    setStats(prev => ({
      ...prev,
      totalThoughts: prev.totalThoughts + thoughts.length,
      thoughtsToday: prev.thoughtsToday + thoughts.length,
    }));
  };

  const sidebarItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'thoughts', icon: FileText, label: 'Thoughts' },
    { id: 'categories', icon: Tag, label: 'Categories' },
    { id: 'users', icon: Users, label: 'Users' },
    { id: 'comments', icon: MessageSquare, label: 'Comments' },
    { id: 'announcements', icon: Bell, label: 'Announcements' },
    { id: 'occasions', icon: Gift, label: 'Occasions' },
    { id: 'images', icon: Image, label: 'Image Library' },
    { id: 'analytics', icon: BarChart3, label: 'Analytics' },
  ];

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 min-h-0 bg-gradient-to-b from-[#2B7A7E] to-[#1e5a5d] text-white flex flex-col">
        {/* Logo */}
        <div className="px-6 py-6 border-b border-white/10 flex flex-col items-center gap-3">
          <img src="/logo.png" alt="TOTD Logo" className="w-28 object-contain" />
          <div className="text-center">
            <h1 className="font-bold text-lg leading-tight">TOTD Admin</h1>
            <p className="text-xs text-white/60 mt-0.5">Thought Management</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-1">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id as TabType)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-white/20 text-white shadow-lg'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium text-sm">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </button>
            );
          })}
        </nav>

        {/* Admin Profile */}
        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-3 bg-white/10 rounded-xl">
            <div className="w-9 h-9 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold">AD</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">Admin User</p>
              <p className="text-xs text-white/60">Super Admin</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-0 overflow-auto bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-8 py-6 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">
                {activeTab === 'settings' ? 'Change Password' : sidebarItems.find(item => item.id === activeTab)?.label}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Manage and monitor your TOTD application
              </p>
            </div>
            <div className="flex items-center gap-3">
              {activeTab === 'thoughts' && (
                <>
                  <button
                    onClick={() => setShowExcelUpload(true)}
                    className="px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#bf9a2d] transition-colors flex items-center gap-2"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Upload Excel
                  </button>
                  <button
                    onClick={() => setShowCreateThought(true)}
                    className="px-4 py-2 bg-[#2B7A7E] text-white rounded-lg hover:bg-[#1e5a5d] transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    New Thought
                  </button>
                </>
              )}
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 text-sm font-medium ${
                  activeTab === 'settings'
                    ? 'bg-[#2B7A7E] text-white border-[#2B7A7E]'
                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <Settings className="w-4 h-4" />
                Change Password
              </button>
              <button
                onClick={onLogout}
                className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-8">
          {activeTab === 'dashboard' && <DashboardView stats={stats} isLoading={isLoadingStats} setActiveTab={setActiveTab} />}
          {activeTab === 'thoughts' && <ThoughtsView searchQuery={searchQuery} setSearchQuery={setSearchQuery} filterStatus={filterStatus} setFilterStatus={setFilterStatus} filterCategory={filterCategory} setFilterCategory={setFilterCategory} refreshThoughts={refreshThoughts} />}
          {activeTab === 'categories' && <CategoriesView setActiveTab={setActiveTab} setFilterCategory={setFilterCategory} />}
          {activeTab === 'users' && <UsersView />}
          {activeTab === 'comments' && <CommentsView />}
          {activeTab === 'announcements' && <AnnouncementsView />}
          {activeTab === 'occasions' && <Occasions />}
          {activeTab === 'images' && <ImageLibrary />}
          {activeTab === 'analytics' && <AnalyticsView />}
          {activeTab === 'settings' && <SettingsView onPasswordChanged={onLogout} />}
        </div>
      </main>

      {/* Modals */}
      {showCreateThought && (
        <CreateThought
          onClose={() => setShowCreateThought(false)}
          onSave={handleSaveThought}
        />
      )}
      {showExcelUpload && (
        <ExcelUpload
          onClose={() => setShowExcelUpload(false)}
          onImport={handleImportThoughts}
        />
      )}
    </div>
  );
}

// Dashboard View Component
function DashboardView({ stats, isLoading: _isLoading, setActiveTab }: { stats: Stats, isLoading: boolean, setActiveTab: (tab: TabType) => void }) {
  const [recentThoughts, setRecentThoughts] = useState<any[]>([]);
  const [isLoadingThoughts, setIsLoadingThoughts] = useState(false);

  // Fetch recent thoughts from backend
  useEffect(() => {
    const fetchRecentThoughts = async () => {
      try {
        setIsLoadingThoughts(true);
        const data = await adminThoughtsApi.getAll({ limit: 10 });

        // Transform backend data to display format, excluding Image Library entries
        const transformedThoughts = data.thoughts
          .filter((thought: any) => thought.author !== 'Image Library' && !thought.text?.startsWith('[Image Library]'))
          .slice(0, 3)
          .map((thought: any) => ({
          id: thought._id,
          title: thought.text.substring(0, 50) + (thought.text.length > 50 ? '...' : ''),
          author: thought.author,
          status: thought.status,
          date: thought.scheduledDate
            ? new Date(thought.scheduledDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
            : formatDate(thought.createdAt),
        }));
        
        setRecentThoughts(transformedThoughts);
      } catch (error) {
        console.error('Failed to fetch recent thoughts:', error);
        toast.error('Failed to load recent thoughts');
        // Use fallback data
        setRecentThoughts([
          { id: '1', title: 'No thoughts available yet', author: 'System', status: 'draft', date: 'Just now' },
        ]);
      } finally {
        setIsLoadingThoughts(false);
      }
    };

    fetchRecentThoughts();
  }, []);

  // Helper function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const statCards = [
    { label: 'Total Thoughts', value: stats.totalThoughts, icon: FileText, color: 'bg-blue-500', change: '+12%' },
    { label: 'Total Users', value: stats.totalUsers.toLocaleString(), icon: Users, color: 'bg-green-500', change: '+23%' },
    { label: 'Total Comments', value: stats.totalComments.toLocaleString(), icon: MessageSquare, color: 'bg-purple-500', change: '+8%' },
    { label: 'Active Users', value: stats.activeUsers.toLocaleString(), icon: TrendingUp, color: 'bg-[#D4AF37]', change: '+15%' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-green-600 text-sm font-semibold bg-green-50 px-2 py-1 rounded">
                  {stat.change}
                </span>
              </div>
              <p className="text-slate-500 text-sm mb-1">{stat.label}</p>
              <p className="text-3xl font-bold text-slate-800">{stat.value}</p>
            </div>
          );
        })}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Thoughts */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-800">Recent Thoughts</h3>
            <button 
              onClick={() => setActiveTab('thoughts')}
              className="text-[#2B7A7E] text-sm font-semibold hover:underline"
            >
              View All
            </button>
          </div>
          <div className="space-y-4">
            {isLoadingThoughts ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B7A7E]"></div>
              </div>
            ) : recentThoughts.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No thoughts yet. Create your first thought!</p>
              </div>
            ) : (
              recentThoughts.map((thought) => (
                <div key={thought.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{thought.title}</p>
                    <p className="text-sm text-slate-500">by {thought.author}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    thought.status === 'published' ? 'bg-green-100 text-green-700' :
                    thought.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {thought.status}
                  </span>
                  <span className="text-xs text-slate-400">{thought.date}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <QuickActionsPanel setActiveTab={setActiveTab} />
      </div>

      {/* Engagement Chart Placeholder */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <h3 className="text-lg font-bold text-slate-800 mb-6">Engagement Overview</h3>
        <div className="h-64 flex items-center justify-center bg-slate-50 rounded-xl">
          <div className="text-center">
            <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-400">Chart visualization would go here</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Quick Actions Panel Component
function QuickActionsPanel({ setActiveTab }: { setActiveTab: (tab: TabType) => void }) {
  const handleExportData = () => {
    setActiveTab('analytics');
  };

  const handleCreateThought = () => {
    setActiveTab('thoughts');
  };

  const handleScheduleThought = () => {
    setActiveTab('thoughts');
  };

  const handleNewAnnouncement = () => {
    setActiveTab('announcements');
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-200">
      <h3 className="text-lg font-bold text-slate-800 mb-6">Quick Actions</h3>
      <div className="space-y-3">
        <button 
          onClick={handleCreateThought}
          className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white rounded-xl hover:shadow-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          <span className="font-medium">Create New Thought</span>
        </button>
        <button 
          onClick={handleScheduleThought}
          className="w-full flex items-center gap-3 p-4 bg-slate-50 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors"
        >
          <Calendar className="w-5 h-5" />
          <span className="font-medium">Schedule Thought</span>
        </button>
        <button 
          onClick={handleNewAnnouncement}
          className="w-full flex items-center gap-3 p-4 bg-slate-50 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors"
        >
          <Bell className="w-5 h-5" />
          <span className="font-medium">New Announcement</span>
        </button>
        <button 
          onClick={handleExportData}
          className="w-full flex items-center gap-3 p-4 bg-slate-50 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors"
        >
          <Download className="w-5 h-5" />
          <span className="font-medium">Export Data</span>
        </button>
      </div>
    </div>
  );
}

// Thoughts View Component
function ThoughtsView({
  searchQuery,
  setSearchQuery,
  filterStatus,
  setFilterStatus,
  filterCategory,
  setFilterCategory,
  refreshThoughts
}: {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filterStatus: 'all' | 'draft' | 'scheduled' | 'published';
  setFilterStatus: (status: 'all' | 'draft' | 'scheduled' | 'published') => void;
  filterCategory: string;
  setFilterCategory: (cat: string) => void;
  refreshThoughts: number;
}) {
  const [thoughts, setThoughts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isFixingStatus, setIsFixingStatus] = useState(false);
  const [viewingThought, setViewingThought] = useState<any>(null);
  const [editingThought, setEditingThought] = useState<any>(null);
  const [viewingThoughtComments, setViewingThoughtComments] = useState<any>(null);
  const [thoughtComments, setThoughtComments] = useState<any[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [viewingLikersThought, setViewingLikersThought] = useState<any>(null);
  const [likers, setLikers] = useState<any[]>([]);
  const [isLoadingLikers, setIsLoadingLikers] = useState(false);
  const [viewingSaversThought, setViewingSaversThought] = useState<any>(null);
  const [savers, setSavers] = useState<any[]>([]);
  const [isLoadingSavers, setIsLoadingSavers] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);

  // Fetch category list for filter dropdown
  useEffect(() => {
    adminCategoriesApi.getAll()
      .then(data => setCategories((data.categories || []).map((c: any) => c.name).sort()))
      .catch(() => {});
  }, []);

  // Reset to page 1 whenever filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterCategory, searchQuery]);

  // Fetch thoughts from API
  useEffect(() => {
    const fetchThoughts = async () => {
      try {
        setIsLoading(true);
        const params: any = {
          page: currentPage,
          limit: 20,
        };

        if (filterStatus !== 'all') {
          params.status = filterStatus;
        }

        if (filterCategory) {
          params.category = filterCategory;
        }

        if (searchQuery) {
          params.search = searchQuery;
        }

        const data = await adminThoughtsApi.getAll(params);

        // Filter out Image Library entries (they are stored as draft thoughts but belong to Image Library only)
        const filteredThoughts = (data.thoughts || []).filter(
          (t: any) => t.author !== 'Image Library' && !t.text?.startsWith('[Image Library]')
        );
        setThoughts(filteredThoughts);
        setTotalPages(data.totalPages);
      } catch (error) {
        console.error('Failed to fetch thoughts:', error);
        toast.error('Failed to load thoughts');
        setThoughts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchThoughts();
  }, [filterStatus, filterCategory, searchQuery, currentPage, refreshThoughts]);

  // Handle delete thought
  const handleDelete = async (id: string, text: string) => {
    if (!confirm(`Are you sure you want to delete this thought?\n\n"${text.substring(0, 100)}..."`)) {
      return;
    }

    try {
      setIsDeleting(id);
      await adminThoughtsApi.delete(id);
      toast.success('Thought deleted successfully');
      
      // Refresh the list
      setThoughts(prev => prev.filter(t => t._id !== id));
    } catch (error) {
      console.error('Failed to delete thought:', error);
      toast.error('Failed to delete thought');
    } finally {
      setIsDeleting(null);
    }
  };

  // Handle view thought
  const handleView = (thought: any) => {
    setViewingThought(thought);
  };

  // Handle edit thought
  const handleEdit = (thought: any) => {
    setEditingThought(thought);
  };

  // Handle save edited thought
  const handleSaveEdit = async () => {
    // Refresh thoughts list
    const params: any = {
      page: currentPage,
      limit: 20,
    };

    if (filterStatus !== 'all') {
      params.status = filterStatus;
    }

    if (searchQuery) {
      params.search = searchQuery;
    }

    const data = await adminThoughtsApi.getAll(params);
    const filteredThoughts = (data.thoughts || []).filter(
      (t: any) => t.author !== 'Image Library' && !t.text?.startsWith('[Image Library]')
    );
    setThoughts(filteredThoughts);
    setEditingThought(null);
  };

  // Handle view comments for a thought
  const handleViewComments = async (thought: any) => {
    setViewingThoughtComments(thought);
    setIsLoadingComments(true);
    setThoughtComments([]);
    try {
      const result = await adminCommentsApi.getAll({ thoughtId: thought._id, limit: 100 });
      setThoughtComments(result.comments || []);
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setIsLoadingComments(false);
    }
  };

  // Handle view likers for a thought
  const handleViewLikers = async (thought: any) => {
    setViewingLikersThought(thought);
    setIsLoadingLikers(true);
    setLikers([]);
    try {
      const result = await adminThoughtsApi.getLikers(thought._id);
      setLikers(result.likers || []);
    } catch (error) {
      toast.error('Failed to load likers');
    } finally {
      setIsLoadingLikers(false);
    }
  };

  // Handle view savers for a thought
  const handleViewSavers = async (thought: any) => {
    setViewingSaversThought(thought);
    setIsLoadingSavers(true);
    setSavers([]);
    try {
      const result = await adminThoughtsApi.getSavers(thought._id);
      setSavers(result.savers || []);
    } catch (error) {
      toast.error('Failed to load savers');
    } finally {
      setIsLoadingSavers(false);
    }
  };

  // Fix status of all thoughts based on scheduled date
  const handleFixStatusByDate = async () => {
    if (!confirm('This will set status to "scheduled" for future thoughts and "published" for past ones, based on their Thought Date. Continue?')) return;
    setIsFixingStatus(true);
    try {
      const data = await adminThoughtsApi.getAll({ limit: 1000 });
      const allThoughts = (data.thoughts || []).filter(
        (t: any) => t.author !== 'Image Library' && !t.text?.startsWith('[Image Library]')
      );
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const toFix = allThoughts.filter((t: any) => {
        if (!t.scheduledDate) return false;
        const d = new Date(t.scheduledDate);
        d.setHours(0, 0, 0, 0);
        const correctStatus = d <= today ? 'published' : 'scheduled';
        return t.status !== correctStatus;
      });

      if (toFix.length === 0) {
        toast.success('All thought statuses are already correct!');
        setIsFixingStatus(false);
        return;
      }

      await Promise.all(
        toFix.map((t: any) => {
          const d = new Date(t.scheduledDate);
          d.setHours(0, 0, 0, 0);
          const correctStatus = d <= today ? 'published' : 'scheduled';
          return adminThoughtsApi.update(t._id, { status: correctStatus });
        })
      );

      toast.success(`Fixed ${toFix.length} thought${toFix.length !== 1 ? 's' : ''}`);
      // Refresh list
      setCurrentPage(1);
    } catch (error) {
      toast.error('Failed to fix statuses');
    } finally {
      setIsFixingStatus(false);
    }
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search thoughts, authors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2B7A7E] focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2B7A7E] bg-white"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}

          {/* Filter + Fix button */}
          <div className="flex gap-2 flex-wrap">
            {(['all', 'published', 'scheduled', 'draft'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-3 rounded-xl font-medium transition-all ${
                  filterStatus === status
                    ? 'bg-[#2B7A7E] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
            <button
              onClick={handleFixStatusByDate}
              disabled={isFixingStatus}
              className="px-4 py-3 rounded-xl font-medium transition-all bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50 flex items-center gap-2"
              title="Set future thoughts to Scheduled, past thoughts to Published"
            >
              <Calendar className="w-4 h-4" />
              {isFixingStatus ? 'Fixing...' : 'Fix Status by Date'}
            </button>
          </div>
        </div>
      </div>

      {/* Thoughts Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2B7A7E] mx-auto mb-4"></div>
              <p className="text-slate-500">Loading thoughts...</p>
            </div>
          </div>
        ) : thoughts.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg mb-2">No thoughts found</p>
            <p className="text-slate-400 text-sm">
              {searchQuery
                ? 'Try a different search term'
                : filterStatus === 'scheduled'
                ? 'Thoughts with future dates will appear here. Upload thoughts with upcoming dates using Excel or create a new thought with a future scheduled date.'
                : filterStatus === 'draft'
                ? 'No draft thoughts. Save a thought as draft to review it later.'
                : 'Create your first thought to get started'}
            </p>
          </div>
        ) : (
          <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Thought</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Date</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Engagement</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {thoughts.map((thought) => (
                  <tr key={thought._id} className="hover:bg-slate-50 transition-colors">
                    {/* Thought: image + text + author + category + tags */}
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-3">
                        {thought.imageUrl ? (
                          <img
                            src={thought.imageUrl}
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400';
                            }}
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                            <FileText className="w-6 h-6 text-slate-400" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-slate-800 line-clamp-2 text-sm leading-snug">{thought.text}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            <span className="text-xs text-slate-500">{thought.author}</span>
                            <span className="text-slate-300">·</span>
                            <span className="px-2 py-0.5 bg-[#2B7A7E]/10 text-[#2B7A7E] rounded-full text-xs font-medium">{thought.category}</span>
                            {thought.tags?.slice(0, 2).map((tag: string, i: number) => (
                              <span key={i} className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs">#{tag}</span>
                            ))}
                            {thought.tags?.length > 2 && (
                              <span className="text-xs text-slate-400">+{thought.tags.length - 2}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                        thought.status === 'published' ? 'bg-green-100 text-green-700' :
                        thought.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                        thought.status === 'archived' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {thought.status}
                      </span>
                    </td>
                    {/* Date: thought date + published on stacked */}
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                      {thought.scheduledDate ? (
                        <span className={thought.status === 'scheduled' ? 'text-blue-600' : ''}>
                          {new Date(thought.scheduledDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                      {thought.publishedAt && (
                        <p className="text-slate-400 mt-0.5">pub: {formatDate(thought.publishedAt)}</p>
                      )}
                    </td>
                    {/* Engagement */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1 text-xs text-slate-600">
                        <div className="flex items-center gap-2">
                          <button title="View likers" className="hover:text-red-500 transition-colors" onClick={() => handleViewLikers(thought)}>
                            ❤️ {thought.stats?.likes || 0}
                          </button>
                          <span title="Comments">💬 {thought.stats?.comments || 0}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span title="Shares">🔗 {thought.stats?.shares || 0}</span>
                          <button title="View savers" className="hover:text-yellow-600 transition-colors" onClick={() => handleViewSavers(thought)}>
                            🔖 {thought.stats?.saves || 0}
                          </button>
                        </div>
                      </div>
                    </td>
                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                          title="View"
                          onClick={() => handleView(thought)}
                        >
                          <Eye className="w-4 h-4 text-slate-500" />
                        </button>
                        <button
                          className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Comments"
                          onClick={() => handleViewComments(thought)}
                        >
                          <MessageSquare className="w-4 h-4 text-blue-500" />
                        </button>
                        <button
                          className="px-3 py-1.5 bg-[#2B7A7E] hover:bg-[#1e5a5d] text-white rounded-lg transition-colors flex items-center gap-1.5 font-medium text-sm"
                          title="Edit"
                          onClick={() => handleEdit(thought)}
                        >
                          <Edit className="w-3.5 h-3.5" />
                          Edit
                        </button>
                        <button
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete"
                          onClick={() => handleDelete(thought._id, thought.text)}
                          disabled={isDeleting === thought._id}
                        >
                          {isDeleting === thought._id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <Trash2 className="w-4 h-4 text-red-500" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-slate-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* View Thought Modal */}
      {viewingThought && (
        <ViewThoughtModal
          thought={viewingThought}
          onClose={() => setViewingThought(null)}
          onViewLikers={(t) => { setViewingThought(null); handleViewLikers(t); }}
          onViewSavers={(t) => { setViewingThought(null); handleViewSavers(t); }}
        />
      )}

      {/* Edit Thought Modal */}
      {editingThought && (
        <CreateThought
          onClose={() => setEditingThought(null)}
          onSave={handleSaveEdit}
          editData={{
            id: editingThought._id,
            text: editingThought.text,
            author: editingThought.author,
            category: editingThought.category,
            imageUrl: editingThought.imageUrl,
            status: editingThought.status,
            tags: editingThought.tags || [],
            scheduledDate: editingThought.scheduledDate ? new Date(editingThought.scheduledDate).toISOString().split('T')[0] : undefined,
            scheduledTime: editingThought.scheduledDate ? new Date(editingThought.scheduledDate).toTimeString().slice(0, 5) : undefined,
          }}
        />
      )}

      {/* Thought Comments Modal */}
      {viewingThoughtComments && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setViewingThoughtComments(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">Comments</h2>
                <p className="text-white/80 text-sm truncate max-w-xs">{viewingThoughtComments.text?.slice(0, 60)}…</p>
              </div>
              <button onClick={() => setViewingThoughtComments(null)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {isLoadingComments ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B7A7E]"></div>
                </div>
              ) : thoughtComments.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No comments yet</p>
                </div>
              ) : (
                thoughtComments.map((comment: any) => (
                  <div key={comment._id} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-[#2B7A7E]/10 flex items-center justify-center text-sm font-bold text-[#2B7A7E]">
                        {(comment.userId?.name || 'A')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{comment.userId?.name || 'Anonymous'}</p>
                        <p className="text-xs text-slate-400">{new Date(comment.createdAt).toLocaleDateString()}</p>
                      </div>
                      <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-medium ${
                        comment.moderationStatus === 'approved' ? 'bg-green-100 text-green-700' :
                        comment.moderationStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                        comment.moderationStatus === 'flagged' ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>{comment.moderationStatus || 'pending'}</span>
                    </div>
                    <p className="text-sm text-slate-700">{comment.text}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      <span>❤️ {Array.isArray(comment.likes) ? comment.likes.length : (comment.likes || 0)}</span>
                      {comment.reportCount > 0 && <span className="text-orange-500">⚠️ {comment.reportCount} reports</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Savers Modal */}
      {viewingSaversThought && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setViewingSaversThought(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#D4AF37] to-[#b8941e] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">Saved by</h2>
                <p className="text-white/80 text-sm truncate max-w-xs">{viewingSaversThought.text?.slice(0, 60)}…</p>
              </div>
              <button onClick={() => setViewingSaversThought(null)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {isLoadingSavers ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
                </div>
              ) : savers.length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  <span className="text-5xl block mb-3">🔖</span>
                  <p>No saves yet</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-500 mb-4">{savers.length} {savers.length === 1 ? 'user' : 'users'} saved this thought</p>
                  {savers.map((user: any) => (
                    <div key={user._id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-sm font-bold text-[#D4AF37] flex-shrink-0">
                        {(user.name || user.phoneNumber || 'U')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{user.name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500 truncate">{user.phoneNumber || user.email || ''}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Likers Modal */}
      {viewingLikersThought && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setViewingLikersThought(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">Liked by</h2>
                <p className="text-white/80 text-sm truncate max-w-xs">{viewingLikersThought.text?.slice(0, 60)}…</p>
              </div>
              <button onClick={() => setViewingLikersThought(null)} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {isLoadingLikers ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2B7A7E]"></div>
                </div>
              ) : likers.length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  <span className="text-5xl block mb-3">❤️</span>
                  <p>No likes yet</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-500 mb-4">{likers.length} {likers.length === 1 ? 'user' : 'users'} liked this thought</p>
                  {likers.map((user: any) => (
                    <div key={user._id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="w-9 h-9 rounded-full bg-[#2B7A7E]/10 flex items-center justify-center text-sm font-bold text-[#2B7A7E] flex-shrink-0">
                        {(user.name || user.phoneNumber || 'U')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{user.name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500 truncate">{user.phoneNumber || user.email || ''}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// View Thought Modal Component
function ViewThoughtModal({ thought, onClose, onViewLikers, onViewSavers }: { thought: any; onClose: () => void; onViewLikers?: (thought: any) => void; onViewSavers?: (thought: any) => void }) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white px-8 py-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">View Thought</h2>
            <p className="text-white/80 text-sm mt-1">Full thought details</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {/* Image */}
          {thought.imageUrl && (
            <div className="mb-6">
              <img
                src={thought.imageUrl}
                alt="Thought"
                className="w-full h-64 object-cover rounded-xl"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800';
                }}
              />
            </div>
          )}

          {/* Thought Text */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-600 mb-2">Thought</label>
            <p className="text-lg text-slate-800 leading-relaxed bg-slate-50 p-4 rounded-xl">
              {thought.text}
            </p>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Author */}
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-2">Author</label>
              <p className="text-slate-800 bg-slate-50 p-3 rounded-xl">{thought.author}</p>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-2">Category</label>
              <span className="inline-block px-4 py-2 bg-[#2B7A7E]/10 text-[#2B7A7E] rounded-xl font-medium">
                {thought.category}
              </span>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-2">Status</label>
              <span className={`inline-block px-4 py-2 rounded-xl font-semibold ${
                thought.status === 'published' ? 'bg-green-100 text-green-700' :
                thought.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                thought.status === 'archived' ? 'bg-orange-100 text-orange-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {thought.status.charAt(0).toUpperCase() + thought.status.slice(1)}
              </span>
            </div>

            {/* Thought Date */}
            {thought.scheduledDate && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">Thought Date</label>
                <p className="text-slate-800 bg-slate-50 p-3 rounded-xl">
                  {new Date(thought.scheduledDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            )}

            {/* Created At */}
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-2">Created</label>
              <p className="text-slate-800 bg-slate-50 p-3 rounded-xl">
                {formatDate(thought.createdAt)}
              </p>
            </div>

            {/* Updated At */}
            {thought.updatedAt && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">Last Updated</label>
                <p className="text-slate-800 bg-slate-50 p-3 rounded-xl">
                  {formatDate(thought.updatedAt)}
                </p>
              </div>
            )}
          </div>

          {/* Engagement Stats */}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-3">Engagement Statistics</label>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
              <button
                className="bg-red-50 p-4 rounded-xl text-center hover:bg-red-100 transition-colors cursor-pointer"
                title="Click to see who liked this"
                onClick={() => onViewLikers && onViewLikers(thought)}
              >
                <div className="text-2xl mb-1">❤️</div>
                <div className="text-2xl font-bold text-slate-800">{thought.stats?.likes || 0}</div>
                <div className="text-xs text-slate-600">Likes</div>
              </button>
              <div className="bg-blue-50 p-4 rounded-xl text-center">
                <div className="text-2xl mb-1">💬</div>
                <div className="text-2xl font-bold text-slate-800">{thought.stats?.comments || 0}</div>
                <div className="text-xs text-slate-600">Comments</div>
              </div>
              <div className="bg-green-50 p-4 rounded-xl text-center">
                <div className="text-2xl mb-1">🔗</div>
                <div className="text-2xl font-bold text-slate-800">{thought.stats?.shares || 0}</div>
                <div className="text-xs text-slate-600">Shares</div>
              </div>
              <button
                className="bg-yellow-50 p-4 rounded-xl text-center hover:bg-yellow-100 transition-colors cursor-pointer"
                title="Click to see who saved this"
                onClick={() => onViewSavers && onViewSavers(thought)}
              >
                <div className="text-2xl mb-1">🔖</div>
                <div className="text-2xl font-bold text-slate-800">{thought.stats?.saves || 0}</div>
                <div className="text-xs text-slate-600">Saves</div>
              </button>
              <div className="bg-purple-50 p-4 rounded-xl text-center">
                <div className="text-2xl mb-1">👁️</div>
                <div className="text-2xl font-bold text-slate-800">{thought.stats?.views || 0}</div>
                <div className="text-xs text-slate-600">Views</div>
              </div>
            </div>
          </div>

          {/* Tags */}
          {thought.tags && thought.tags.length > 0 && (
            <div className="mt-6">
              <label className="block text-sm font-semibold text-slate-600 mb-2">Tags</label>
              <div className="flex flex-wrap gap-2">
                {thought.tags.map((tag: string, index: number) => (
                  <span key={index} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-8 py-4 bg-slate-50">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-[#2B7A7E] text-white rounded-xl hover:bg-[#1e5a5d] transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Categories View Component
function CategoriesView({ setActiveTab, setFilterCategory }: {
  setActiveTab: (tab: TabType) => void;
  setFilterCategory: (cat: string) => void;
}) {
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [thoughtCounts, setThoughtCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const result = await adminCategoriesApi.getAll();
      if (result && result.categories) {
        setCategories(result.categories);
        
        // Fetch thought counts for each category
        const counts: Record<string, number> = {};
        for (const category of result.categories) {
          try {
            const thoughtsResult = await adminThoughtsApi.getAll({ category: category.name, limit: 1 });
            counts[category._id] = thoughtsResult.total || 0;
          } catch (error) {
            counts[category._id] = 0;
          }
        }
        setThoughtCounts(counts);
      }
    } catch (error: any) {
      console.error('Failed to fetch categories:', error);
      toast.error(error.message || 'Failed to load categories');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCategory = () => {
    setShowAddModal(true);
  };

  const handleEditCategory = (category: any) => {
    setEditingCategory(category);
    setShowEditModal(true);
  };

  const handleDeleteCategory = async (category: any) => {
    if (!confirm(`Are you sure you want to delete "${category.name}" category?`)) {
      return;
    }

    try {
      await adminCategoriesApi.delete(category._id);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error: any) {
      console.error('Delete category error:', error);
      toast.error(error.message || 'Failed to delete category');
    }
  };

  const handleSaveCategory = async (data: any) => {
    try {
      await adminCategoriesApi.create(data);
      toast.success('Category created successfully');
      setShowAddModal(false);
      fetchCategories();
    } catch (error: any) {
      console.error('Create category error:', error);
      toast.error(error.message || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async (data: any) => {
    try {
      await adminCategoriesApi.update(editingCategory._id, data);
      toast.success('Category updated successfully');
      setShowEditModal(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error: any) {
      console.error('Update category error:', error);
      toast.error(error.message || 'Failed to update category');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-slate-800">All Categories</h3>
            {!isLoading && (
              <p className="text-sm text-slate-500 mt-0.5">{categories.length} {categories.length === 1 ? 'category' : 'categories'} total</p>
            )}
          </div>
          <button
            onClick={handleAddCategory}
            className="px-4 py-2 bg-[#2B7A7E] text-white rounded-lg hover:bg-[#1e5a5d] transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Category
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#2B7A7E] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            No categories yet. Click &quot;Add Category&quot; to create one.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <div key={category._id} className="p-6 border-2 border-slate-200 rounded-xl hover:border-[#2B7A7E] transition-all hover:shadow-md">
                <div className="flex items-start justify-between mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center" 
                    style={{ backgroundColor: (category.color || '#2B7A7E') + '20' }}
                  >
                    <Tag className="w-6 h-6" style={{ color: category.color || '#2B7A7E' }} />
                  </div>
                  <div className="flex gap-1">
                    <button 
                      onClick={() => handleEditCategory(category)}
                      className="p-1.5 hover:bg-slate-100 rounded"
                    >
                      <Edit className="w-4 h-4 text-slate-600" />
                    </button>
                    <button 
                      onClick={() => handleDeleteCategory(category)}
                      className="p-1.5 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                </div>
                <h4 className="font-bold text-slate-800 mb-1">{category.name}</h4>
                {category.description && (
                  <p className="text-xs text-slate-500 mb-2">{category.description}</p>
                )}
                <div className="flex items-center justify-between mt-2">
                  <p className="text-sm text-slate-500">{thoughtCounts[category._id] || 0} thoughts</p>
                  {(thoughtCounts[category._id] || 0) > 0 && (
                    <button
                      onClick={() => {
                        setFilterCategory(category.name);
                        setActiveTab('thoughts');
                      }}
                      className="text-xs px-3 py-1.5 bg-[#2B7A7E]/10 text-[#2B7A7E] rounded-lg hover:bg-[#2B7A7E]/20 transition-colors font-medium flex items-center gap-1"
                    >
                      <FileText className="w-3 h-3" />
                      View Thoughts
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <CategoryModal
          onClose={() => setShowAddModal(false)}
          onSave={handleSaveCategory}
        />
      )}

      {/* Edit Category Modal */}
      {showEditModal && editingCategory && (
        <CategoryModal
          category={editingCategory}
          onClose={() => {
            setShowEditModal(false);
            setEditingCategory(null);
          }}
          onSave={handleUpdateCategory}
        />
      )}
    </div>
  );
}

// Category Modal Component
function CategoryModal({ category, onClose, onSave }: {
  category?: any;
  onClose: () => void;
  onSave: (data: any) => Promise<void> | void;
}) {
  const [name, setName] = useState(category?.name || '');
  const [description, setDescription] = useState(category?.description || '');
  const [color, setColor] = useState(category?.color || '#2B7A7E');
  const [order, setOrder] = useState(category?.order || 0);
  const [isSaving, setIsSaving] = useState(false);

  const predefinedColors = [
    '#2B7A7E', '#D4AF37', '#8B5CF6', '#10B981', '#F59E0B',
    '#EF4444', '#3B82F6', '#EC4899', '#14B8A6', '#F97316'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        color,
        order
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold">
            {category ? 'Edit Category' : 'Add New Category'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Category Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Motivation, Wisdom, Life"
              className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-lg focus:border-[#2B7A7E] focus:outline-none transition-colors"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this category"
              rows={3}
              className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-lg focus:border-[#2B7A7E] focus:outline-none transition-colors resize-none"
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Color
            </label>
            <div className="flex flex-wrap gap-3 mb-3">
              {predefinedColors.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-10 h-10 rounded-lg transition-all ${
                    color === c ? 'ring-4 ring-offset-2 ring-slate-400' : 'hover:scale-110'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-full h-10 rounded-lg border-2 border-slate-200 cursor-pointer"
            />
          </div>

          {/* Order */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Display Order
            </label>
            <input
              type="number"
              value={order}
              onChange={(e) => setOrder(parseInt(e.target.value))}
              min="0"
              className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-lg focus:border-[#2B7A7E] focus:outline-none transition-colors"
            />
            <p className="text-xs text-slate-500 mt-1">Lower numbers appear first</p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#2B7A7E] to-[#1e5a5d] text-white rounded-lg hover:shadow-lg transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                category ? 'Update' : 'Create'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Placeholder Views
function UsersView() {
  return <UserManagement />;
}

function CommentsView() {
  return <CommentModeration />;
}

function AnnouncementsView() {
  return <Announcements />;
}

function AnalyticsView() {
  return <Analytics />;
}

function SettingsView({ onPasswordChanged }: { onPasswordChanged: () => void }) {
  return <SettingsComponent onPasswordChanged={onPasswordChanged} />;
}